import { useCallback, useEffect, useState } from 'react';
import auth from '../../service/auth';
import { PageHeader, Card, Badge, Spinner, Input, Select, Button, Pagination, formatDate } from '../../components/ui';

const LIMIT = 50;

function Tokens() {
  const [data, setData] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [appId, setAppId] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    auth.api('lists').then((lists) => setApps(lists.apps || []));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: LIMIT, offset });
    if (appId) params.set('app_id', appId);
    if (search) params.set('q', search);
    const result = await auth.api('tokens/page?' + params.toString());
    setData(result);
    setLoading(false);
  }, [offset, appId, search]);

  useEffect(() => { load(); }, [load]);

  const remove = async (token) => {
    if (!window.confirm("Supprimer ce token ?")) return;
    await auth.api('token/' + encodeURIComponent(token), { method: "DELETE" });
    await load();
  };

  const onSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    setSearch(searchInput);
  };

  return (
    <div>
      <PageHeader
        title="Tokens"
        subtitle={data ? `${data.total.toLocaleString("fr-FR")} tokens enregistrés` : "Chargement…"}
      />

      <Card className="p-4 mb-4">
        <form onSubmit={onSearch} className="flex flex-wrap items-center gap-2">
          <Select value={appId} onChange={(e) => { setOffset(0); setAppId(e.target.value); }}>
            <option value="">Toutes les apps</option>
            {apps.map((a) => <option key={a.app_id} value={a.app_id}>{a.name}</option>)}
          </Select>
          <Input
            placeholder="Rechercher un token ou device_id…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="flex-1 min-w-[200px]"
          />
          <Button type="submit">Rechercher</Button>
          {search && (
            <Button variant="ghost" onClick={() => { setSearch(""); setSearchInput(""); setOffset(0); }}>
              Réinitialiser
            </Button>
          )}
        </form>
      </Card>

      <Card>
        {loading ? <Spinner /> : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500">
                    <th className="px-4 py-3 font-medium">ID</th>
                    <th className="px-4 py-3 font-medium">App</th>
                    <th className="px-4 py-3 font-medium">Token</th>
                    <th className="px-4 py-3 font-medium">Profil</th>
                    <th className="px-4 py-3 font-medium">Statut</th>
                    <th className="px-4 py-3 font-medium">Créé le</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((t) => (
                    <tr key={t.token_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-400">{t.token_id}</td>
                      <td className="px-4 py-3"><Badge color="indigo">{t.app_name || t.app_id}</Badge></td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600 max-w-[220px] truncate" title={t.token}>
                        {t.token}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 max-w-[160px] truncate" title={t.profile}>
                        {t.profile || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {t.enabled ? <Badge color="green">actif</Badge> : <Badge color="red">désactivé</Badge>}
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(t.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="danger" className="!px-2.5 !py-1 text-xs" onClick={() => remove(t.token)}>
                          Retirer
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {data.rows.length === 0 && (
                    <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">Aucun token trouvé</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-slate-200">
              <Pagination total={data.total} limit={LIMIT} offset={offset} onPage={setOffset} />
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

export default Tokens;
